import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  saveCurrentMarket,
  getCurrentMarket,
  clearCurrentMarket,
  type CurrentMarketInfo,
} from '@paulownia/shared/lib/storage';

interface CurrentMarketContextValue {
  currentMarket: CurrentMarketInfo | null;
  isLoading: boolean;
  setMarket: (market: CurrentMarketInfo) => Promise<void>;
  clearMarket: () => Promise<void>;
}

const CurrentMarketContext = createContext<CurrentMarketContextValue | null>(null);

export function CurrentMarketProvider({ children }: { children: ReactNode }) {
  const [currentMarket, setCurrentMarket] = useState<CurrentMarketInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getCurrentMarket()
      .then((market) => {
        setCurrentMarket(market);
      })
      .catch((error) => {
        console.error('Error loading current market:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const setMarket = useCallback(async (market: CurrentMarketInfo) => {
    setCurrentMarket(market);
    await saveCurrentMarket(market);
  }, []);

  const clearMarket = useCallback(async () => {
    setCurrentMarket(null);
    await clearCurrentMarket();
  }, []);

  return (
    <CurrentMarketContext.Provider value={{ currentMarket, isLoading, setMarket, clearMarket }}>
      {children}
    </CurrentMarketContext.Provider>
  );
}

export function useCurrentMarket() {
  const context = useContext(CurrentMarketContext);
  if (!context) {
    throw new Error('useCurrentMarket must be used within a CurrentMarketProvider');
  }
  return context;
}
